import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { postsAPI, interactionsAPI, hubError } from '../lib/api';
import { useLiveEvents } from '../context/LiveEventsContext';
import { useInteractionTracker, useItemTimeTracker } from '../hooks/useInteractionTracker';
import { TopBar } from '../components/layout/TopBar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { FileText, Plus, Edit, Trash2, Loader2, Video, ExternalLink, Eye, MousePointer, Users, Search } from 'lucide-react';
import { toast } from 'sonner';
import { MediaUploader, MediaPreview } from '../components/MediaUploader';
import { Pagination } from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';

const emptyForm = { title: '', description: '', content: '', category: '', image_url: '', video: '', video_thumbnail: '' };
const LIVE_POST_EVENTS = ['post.created'];

export default function PostsPage() {
  const { selectedChurchId, canCreate, canUpdate, canDelete, canRead } = useAuth();
  const { setMobileMenuOpen, churchInfo } = useOutletContext();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [badges, setBadges] = useState({});

  const pg = usePagination(`${applied}|${selectedChurchId}`);

  const { trackClick } = useInteractionTracker('post', null, null, selectedChurchId, { trackPageView: false, trackTimeSpent: false });
  useItemTimeTracker('post', editing, form.title, selectedChurchId, dialogOpen);

  const fetchItems = useCallback(() => {
    setLoading(true);
    postsAPI.list(applied || undefined, pg.params)
      .then(res => {
        const data = res.data?.data || [];
        setItems(data);
        setTotal(res.data?.total || 0);
        if (data.length > 0 && canRead('analytics')) {
          interactionsAPI.badges('post', data.map(e => e.id), selectedChurchId)
            .then(bRes => setBadges(bRes.data?.data || {}))
            .catch(() => {});
        }
      })
      .catch(err => toast.error(hubError(err, 'Error al cargar noticias')))
      .finally(() => setLoading(false));
  }, [canRead, selectedChurchId, applied, pg.params]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  useLiveEvents(LIVE_POST_EVENTS, (events) => {
    toast.info(`Nueva noticia publicada: ${events[events.length - 1].payload?.title || ''}`);
    fetchItems();
  });

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (item) => {
    trackClick(item.id, item.title);
    setEditing(item.id);
    setForm({
      title: item.title || '', description: item.description || '', content: item.content || '',
      category: item.category || '', image_url: item.image_url || '', video: item.video || '',
      video_thumbnail: item.video_thumbnail || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title) { toast.error('Título requerido'); return; }
    setSaving(true);
    const payload = {
      ...form,
      image_url: form.image_url || null,
      video: form.video || null,
      video_thumbnail: form.video ? (form.video_thumbnail || null) : null,
      category: form.category || null,
    };
    try {
      if (editing) { await postsAPI.update(editing, payload); toast.success('Noticia actualizada'); }
      else { await postsAPI.create(payload); toast.success('Noticia publicada'); }
      setDialogOpen(false); fetchItems();
    } catch (err) { toast.error(hubError(err, 'Error al guardar')); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try { await postsAPI.delete(id); toast.success('Noticia eliminada'); fetchItems(); }
    catch (err) { toast.error(hubError(err, 'Error al eliminar')); }
  };

  return (
    <div>
      <TopBar title="Noticias" onMenuClick={() => setMobileMenuOpen(true)} churchInfo={churchInfo}
        actionButton={canCreate('posts') && <Button className="bg-[#056A84] hover:bg-[#045A70] text-white btn-lift" onClick={openCreate} data-testid="posts-create-button"><Plus className="w-4 h-4 mr-1" /> Nueva</Button>}
      />
      <div className="px-4 sm:px-6 lg:px-8 py-5">
        <div className="relative mb-4 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && setApplied(search)}
            placeholder="Buscar noticias..."
            className="pl-9"
            data-testid="posts-search-input"
          />
        </div>
        <Pagination
          total={total} page={pg.page} pageSize={pg.pageSize}
          onPageChange={pg.setPage} onPageSizeChange={pg.setPageSize}
          label="noticias" testId="posts-pagination"
        />
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 spinner text-[#056A84]" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state"><FileText className="w-12 h-12 text-muted-foreground/40" /><p>No hay noticias</p>{canCreate('posts') && <Button className="bg-[#056A84] hover:bg-[#045A70] text-white mt-2" onClick={openCreate}>Crear noticia</Button>}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(item => {
              const b = badges[String(item.id)];
              return (
                <Card key={item.id} className="stat-card overflow-hidden">
                  {(item.image_url || item.video_thumbnail) && (
                    <img src={item.image_url || item.video_thumbnail} alt={item.title} className="w-full h-36 object-cover" />
                  )}
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-[#2B7DBA]/10 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-[#2B7DBA]" />
                        </div>
                        {item.category && <Badge variant="secondary" className="text-[10px]">{item.category}</Badge>}
                      </div>
                      <div className="flex gap-1">
                        {canUpdate('posts') && <Button variant="ghost" size="sm" onClick={() => openEdit(item)} data-testid={`post-edit-${item.id}`}><Edit className="w-4 h-4" /></Button>}
                        {canDelete('posts') && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button variant="ghost" size="sm" data-testid={`post-delete-${item.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>¿Eliminar noticia?</AlertDialogTitle><AlertDialogDescription>No se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(item.id)} className="bg-destructive text-destructive-foreground">Eliminar</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </div>
                    <h3 className="font-semibold text-sm mb-1">{item.title}</h3>
                    {item.description && <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{item.description}</p>}
                    {item.video && (
                      <a href={item.video} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#056A84] hover:underline">
                        <Video className="w-3 h-3" /> Ver video <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50">
                      <p className="text-[10px] text-muted-foreground">
                        {item.created_at ? new Date(item.created_at).toLocaleDateString('es-ES') : ''}
                        {typeof item.amen_count === 'number' && ` · ${item.amen_count} amén`}
                      </p>
                      {b && (
                        <TooltipProvider>
                          <div className="flex items-center gap-2" data-testid={`post-badges-${item.id}`}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                                  <Eye className="w-3 h-3 text-[#056A84]" /><span className="font-medium">{b.views}</span>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent><p>Vistas</p></TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                                  <MousePointer className="w-3 h-3 text-[#1F9D7A]" /><span className="font-medium">{b.clicks}</span>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent><p>Clics</p></TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
                                  <Users className="w-3 h-3 text-[#2B7DBA]" /><span className="font-medium">{b.unique_users}</span>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent><p>Usuarios únicos</p></TooltipContent>
                            </Tooltip>
                          </div>
                        </TooltipProvider>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Nueva'} Noticia</DialogTitle><DialogDescription className="sr-only">Formulario de noticia</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Título *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="post-title-input" /></div>
            <div className="space-y-2"><Label>Resumen</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} data-testid="post-description-input" /></div>
            <div className="space-y-2"><Label>Contenido</Label><Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={5} data-testid="post-content-input" /></div>
            <div className="space-y-2"><Label>Categoría</Label><Input value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="Testimonios, Cultos..." data-testid="post-category-input" /></div>
            <div className="space-y-2">
              <Label>Imagen principal</Label>
              <MediaUploader kind="image" testId="post-image-upload"
                onUploaded={(res) => setForm(f => ({ ...f, image_url: res.url || '' }))} />
              <Input value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} placeholder="o pega una URL: https://..." data-testid="post-image-input" />
              <MediaPreview url={form.image_url} kind="image" testId="post-image-preview"
                onClear={() => setForm(f => ({ ...f, image_url: '' }))} />
            </div>

            <div className="space-y-2">
              <Label>Video</Label>
              <MediaUploader kind="video" testId="post-video-upload"
                onUploaded={(res) => setForm(f => ({
                  ...f,
                  video: res.url || '',
                  video_thumbnail: res.poster_url || f.video_thumbnail || '',
                }))} />
              <Input value={form.video} onChange={e => setForm({...form, video: e.target.value})} placeholder="o pega el enlace de YouTube, Instagram, TikTok o MP4" data-testid="post-video-input" />
              <MediaPreview url={form.video && form.video.endsWith('.mp4') ? form.video : null} kind="video" testId="post-video-preview"
                onClear={() => setForm(f => ({ ...f, video: '', video_thumbnail: '' }))} />
            </div>

            {form.video && (
              <div className="space-y-2">
                <Label>Portada del video</Label>
                <MediaUploader kind="image" testId="post-thumb-upload"
                  onUploaded={(res) => setForm(f => ({ ...f, video_thumbnail: res.url || '' }))} />
                <Input value={form.video_thumbnail} onChange={e => setForm({...form, video_thumbnail: e.target.value})} placeholder="https://... (se completa sola al subir el video)" data-testid="post-thumbnail-input" />
                <MediaPreview url={form.video_thumbnail} kind="image" testId="post-thumbnail-preview"
                  onClear={() => setForm(f => ({ ...f, video_thumbnail: '' }))} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#056A84] hover:bg-[#045A70] text-white" onClick={handleSave} disabled={saving} data-testid="post-save-button">{saving ? <Loader2 className="w-4 h-4 spinner mr-1" /> : null}{editing ? 'Actualizar' : 'Publicar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
